import dayjs from 'dayjs';

import constants from './constants';

const formatLocalPrice = (num, precision = 2, locale = 'en-IN') => {
  // Check if the number is a whole number
  const isWholeNumber = Number.isInteger(num);

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: isWholeNumber ? 0 : precision,
    maximumFractionDigits: precision,
  });
  return formatter.format(num);
};

const getDateTime = (date, format = 'h:mm A, DD MMMM YYYY') => {
  return dayjs(date).format(format);
};

const fixToTwoLocalPrice = num => formatLocalPrice(num);
const fixToThreeLocalPrice = num => formatLocalPrice(num, 3);

const getDate = (date, format = 'DD MMMM YYYY') => {
  return dayjs(date).format(format);
};

const formatCurrency = (num, locale = 'en-IN') => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'INR',
  });
  return formatter.format(num);
};

// Variable name for formatting currency to M, B etc instead of direct numbers
const formatCurrencyToShort = (num, locale = 'en-IN', _compactDisplay = 'short') => {
  const crore = 10000000; // 1 crore = 1,00,00,000
  const lakh = 100000; // 1 lakh = 1,00,000
  const thousand = 1000; // 1 thousand = 1,000

  if (num >= crore) {
    const crores = num / crore;
    const formattedCrores = Math.round(crores).toLocaleString(locale);
    return `₹${formattedCrores} Cr`;
  } else if (num >= lakh) {
    const lakhs = num / lakh;
    const formattedLakhs = Math.round(lakhs).toLocaleString(locale);
    return `₹${formattedLakhs}L`;
  } else {
    const thousands = num / thousand;
    const formattedThousands = Math.round(thousands).toLocaleString(locale);
    return `₹${formattedThousands}k`;
  }
};

const formatNumber = (num, locale = 'en-IN') => {
  const formatter = new Intl.NumberFormat(locale);
  return formatter.format(num);
};

const formatJsonSafely = (str: string) => {
  try {
    const jsonValue = JSON.parse(str);
    return jsonValue;
  } catch {
    return undefined;
  }
};

const formatRawHtmlValue = (rawHtml: string = '') => {
  return formatJsonSafely(rawHtml)?.hasOwnProperty('time')
    ? JSON.parse(rawHtml) || ''
    : {
        time: new Date().getTime(),
        blocks: [
          {
            id: new Date().toISOString(),
            type: 'paragraph',
            data: {
              text: rawHtml || '',
            },
          },
        ],
        version: constants.TEXT_EDITOR_VERSION,
      };
};

function getInvestedAmountRange(amount: number | null | undefined) {
  if (!amount) return '0 - ₹2.5L';
  const amountNumber = Number(amount);
  if (amountNumber < 250000) return '0 - ₹2.5L';
  if (amountNumber < 1000000) return '₹2.5L - ₹10L';
  if (amountNumber < 2500000) return '₹10L - ₹25L';
  if (amountNumber < 5000000) return '₹25L - ₹50L';
  if (amountNumber < 10000000) return '₹50L - ₹1Cr';
  return '₹1Cr+';
}

function getEndOfLastQuarter(): Date {
  const now = dayjs();
  const month = now.month() + 1; // 1-12
  let year = now.year();
  // Determine last quarter's end month
  const endMonth = month <= 3 ? (year--, 12) : month <= 6 ? 3 : month <= 9 ? 6 : 9;
  // Return the last day of that month
  return dayjs(`${year}-${String(endMonth).padStart(2, '0')}-01`)
    .endOf('month')
    .toDate();
}

const toTitleCase = (value?: string | null) =>
  value
    ? value
        .toLowerCase()
        .split('_')
        .map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
        .join(' ')
    : '—';

const padNumber = (num: number): string => {
  if (num < 10 && num > 0) return `0${num}`;
  return num.toString();
};

export {
  fixToTwoLocalPrice,
  fixToThreeLocalPrice,
  getDateTime,
  getDate,
  formatCurrency,
  formatCurrencyToShort,
  formatNumber,
  formatJsonSafely,
  formatRawHtmlValue,
  getInvestedAmountRange,
  getEndOfLastQuarter,
  formatLocalPrice,
  toTitleCase,
  padNumber,
};
